<script setup lang="ts">
import type { DiagnosticsSnapshot } from '@/lib/types';

defineProps<{ snapshot: DiagnosticsSnapshot }>();

function formatBytes(n: number | null): string {
  if (n === null) return '—';
  const mb = n / (1024 * 1024);
  return `${mb.toFixed(1)} MiB`;
}
function formatRemaining(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${Math.floor(ms / 1000)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}
</script>

<template>
  <div class="overlay" role="status" aria-live="polite">
    <div class="row">
      <span class="k">build</span><span class="v">{{ snapshot.buildVersion }}</span>
    </div>
    <div class="row">
      <span class="k">device</span><span class="v">{{ snapshot.deviceId ?? '—' }}</span>
    </div>
    <div class="row">
      <span class="k">screen</span><span class="v">{{ snapshot.screenId ?? '—' }}</span>
    </div>
    <div class="row">
      <span class="k">segment</span><span class="v">{{ snapshot.currentSegmentId ?? 'idle' }}</span>
    </div>
    <div class="row">
      <span class="k">remaining</span
      ><span class="v">{{ formatRemaining(snapshot.currentSegmentRemainingMs) }}</span>
    </div>
    <div class="row">
      <span class="k">item</span><span class="v">{{ snapshot.currentItemId ?? '—' }}</span>
    </div>
    <div class="row">
      <span class="k">signal</span
      ><span class="v" :class="{ degraded: snapshot.signalOverride !== null }">{{
        snapshot.signalOverride ?? 'none'
      }}</span>
    </div>
    <div class="row">
      <span class="k">net</span
      ><span class="v" :class="snapshot.connectivity">{{ snapshot.connectivity }}</span>
    </div>
    <div class="row">
      <span class="k">last ack</span><span class="v">{{ snapshot.lastHeartbeatAckAt ?? '—' }}</span>
    </div>
    <div class="row">
      <span class="k">cache</span><span class="v">{{ formatBytes(snapshot.cacheBytes) }}</span>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 9999;
  background: rgba(10, 8, 7, 0.92);
  border: 1px solid rgba(226, 180, 78, 0.4);
  padding: 14px 16px;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  color: #f6eedc;
  min-width: 280px;
  pointer-events: none;
  letter-spacing: 0.04em;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  animation: fade-in 200ms ease-out;
}
.row {
  display: grid;
  grid-template-columns: 84px 1fr;
  gap: 12px;
  padding: 2px 0;
}
.k {
  color: #e2b44e;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 9px;
  align-self: center;
}
.v {
  color: #f6eedc;
  word-break: break-all;
}
.v.online {
  color: #b9e4a3;
}
.v.degraded {
  color: #e2b44e;
}
.v.offline {
  color: #f5b8a0;
}
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
